import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import ChartContainer from './ChartContainer';

// Interface para os dados de análise por faixa
interface RangeAnalysisData {
  name: string;
  value: number;
  color: string;
  percentage?: number;
}

// Interface para definir o tipo de uma faixa
interface Range {
  min: number;
  max: number;
  name: string;
}

// Interface para as propriedades do componente
interface InteractionRangeAnalysisProps {
  data: any[]; // Dados para a análise (bots e suas interações)
  timeFrame: 'daily' | 'weekly' | 'monthly'; // Período de tempo
  loading: boolean;
}

// Cores para cada faixa
const RANGE_COLORS = [
  '#8884d8', // 0-10
  '#82ca9d', // 10-30
  '#ffc658', // 30-60
  '#ff8042', // 60-100
  '#0088fe', // >100
];

// Componente customizado para o tooltip
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-gray-800 p-2 border border-gray-700 rounded shadow-lg">
        <p className="font-medium text-sm">{`${payload[0].name}: ${payload[0].value} bots`}</p>
        <p className="text-xs text-gray-400">{`${payload[0].payload.percentage.toFixed(2)}% do total`}</p>
      </div>
    );
  }
  return null;
};

const InteractionRangeAnalysis: React.FC<InteractionRangeAnalysisProps> = ({ 
  data, 
  timeFrame,
  loading 
}) => {
  // Processa os dados para categorizar nas faixas de interações
  const rangeData = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    // Definir os limites das faixas com base no timeFrame
    let ranges: Range[];
    let title: string;
    
    switch (timeFrame) {
      case 'weekly':
        ranges = [
          { min: 0, max: 70, name: '0-70 interações' },
          { min: 70, max: 210, name: '70-210 interações' },
          { min: 210, max: 420, name: '210-420 interações' },
          { min: 420, max: 700, name: '420-700 interações' },
          { min: 700, max: Infinity, name: 'Mais de 700 interações' }
        ];
        title = "Distribuição Semanal de Bots por Faixa de Interações";
        break;
      case 'monthly':
        ranges = [
          { min: 0, max: 300, name: '0-300 interações' },
          { min: 300, max: 900, name: '300-900 interações' },
          { min: 900, max: 1800, name: '900-1800 interações' },
          { min: 1800, max: 3000, name: '1800-3000 interações' },
          { min: 3000, max: Infinity, name: 'Mais de 3000 interações' }
        ];
        title = "Distribuição Mensal de Bots por Faixa de Interações";
        break;
      case 'daily':
      default:
        ranges = [
          { min: 0, max: 10, name: '0-10 interações' },
          { min: 10, max: 30, name: '10-30 interações' },
          { min: 30, max: 60, name: '30-60 interações' },
          { min: 60, max: 100, name: '60-100 interações' },
          { min: 100, max: Infinity, name: 'Mais de 100 interações' }
        ];
        title = "Distribuição Diária de Bots por Faixa de Interações";
    }

    // Contagem por faixa
    const rangeCounts = ranges.map(range => ({
      name: range.name,
      value: 0,
      color: RANGE_COLORS[ranges.indexOf(range)]
    }));

    // Processar cada bot para contabilizar em qual faixa ele se encaixa
    data.forEach(bot => {
      // Verificar se é um valor válido
      const interactions = typeof bot.total_interactions === 'string' 
        ? parseInt(bot.total_interactions) 
        : (bot.total_interactions || 0);
      
      // Determinar em qual faixa o bot se encaixa
      for (let i = 0; i < ranges.length; i++) {
        if (interactions >= ranges[i].min && interactions < ranges[i].max) {
          rangeCounts[i].value++;
          break;
        }
      }
    });

    // Calcular a porcentagem para cada faixa
    const total = rangeCounts.reduce((acc, item) => acc + item.value, 0);
    return rangeCounts.map(item => ({
      ...item,
      percentage: (item.value / total) * 100
    }));
  }, [data, timeFrame]);

  // Título com base no timeFrame
  const getTitle = () => {
    switch (timeFrame) {
      case 'weekly':
        return "Distribuição Semanal de Bots por Faixa de Interações";
      case 'monthly':
        return "Distribuição Mensal de Bots por Faixa de Interações";
      case 'daily':
      default:
        return "Distribuição Diária de Bots por Faixa de Interações";
    }
  };

  return (
    <ChartContainer
      title={getTitle()}
      tooltip="Distribuição de bots por faixas de interação. Mostra quantos bots estão em cada faixa de volume de interações."
      height={350}
    >
      {loading ? (
        <div className="flex justify-center items-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : rangeData && rangeData.length > 0 ? (
        <div className="flex flex-col md:flex-row items-center justify-between h-full">
          <ResponsiveContainer width="55%" height={300}>
            <PieChart>
              <Pie
                data={rangeData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={90}
                innerRadius={40}
                paddingAngle={5}
                dataKey="value"
              >
                {rangeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                layout="vertical" 
                align="right" 
                verticalAlign="middle" 
                formatter={(value) => <span className="text-xs">{value}</span>}
                wrapperStyle={{ paddingLeft: 20 }}
              />
            </PieChart>
          </ResponsiveContainer>
          
          <div className="w-full md:w-40% mt-4 md:mt-0 md:ml-4">
            <div className="bg-gray-800 rounded-lg p-4">
              <h4 className="text-gray-200 font-medium mb-2">Resumo por Faixa</h4>
              <div className="space-y-3">
                {rangeData.map((range, index) => (
                  <div key={`summary-${index}`} className="flex flex-col">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div 
                          className="w-3 h-3 rounded-full mr-2" 
                          style={{ backgroundColor: range.color }}
                        ></div>
                        <span className="text-sm text-gray-300 truncate max-w-[120px]">{range.name}</span>
                      </div>
                      <span className="text-sm font-medium ml-2">{range.value} bots</span>
                    </div>
                    <div className="mt-1 w-full bg-gray-700 rounded-full h-1.5">
                      <div 
                        className="h-1.5 rounded-full" 
                        style={{ width: `${range.percentage}%`, backgroundColor: range.color }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex justify-center items-center h-full text-gray-400">
          Nenhum dado disponível para análise
        </div>
      )}
    </ChartContainer>
  );
};

export default InteractionRangeAnalysis; 